/**
 * Cash forecast, with the assumptions panel beside it.
 *
 * The panel is the point of the page. A forecast whose inputs are invisible is a number to
 * argue with; a forecast that shows every input, where it came from, and whether it was
 * computed or typed, is a number to plan with. Each row states its mode and the window the
 * AUTO value was taken from, exactly as the run returned them, so the panel is a view of
 * what actually ran rather than a second copy of the defaults.
 */
import { useState } from 'react';
import { Card, EmptyState, ErrorState, Pill, Skeleton, DataTable, MoneyText, type Column } from '@/components';
import { formatMoney } from '@/lib/format';
import { useForecast } from './api';
import { LineChart } from './charts';
import type { Assumption, ForecastDay } from './types';
import './finance.css';

const HORIZONS = [30, 60, 90] as const;

export function ForecastPage() {
  const [horizon, setHorizon] = useState<number>(90);
  const scenario = 'base';
  const query = useForecast(horizon, scenario);

  if (query.isLoading) return <Skeleton shape="card" />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        message="The forecast could not be built."
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const data = query.data;
  const points = data.days.map((day) => ({ label: day.date, value: day.closing_cents }));

  // Only outflows large enough to move the line get a marker. A chart with a label on every
  // fixed cost is a chart nobody can read.
  const threshold = Math.max(100_000, Math.abs(data.opening_cash_cents) * 0.05);
  const markers = data.days
    .flatMap((day) =>
      day.events
        .filter((event) => Math.abs(event.amount_cents) >= threshold)
        .map((event) => ({
          at: day.date,
          label: `${event.label} ${formatMoney(Math.abs(event.amount_cents), { showCents: false })}`,
          tone:
            event.kind === 'tax'
              ? ('crit' as const)
              : event.kind === 'po'
                ? ('copper' as const)
                : event.amount_cents > 0
                  ? ('info' as const)
                  : ('warn' as const),
        })),
    )
    .slice(0, 8);

  return (
    <div className="o-stack">
      <div className="o-chiprow" data-allow-hscroll="true">
        {HORIZONS.map((h) => (
          <button
            key={h}
            type="button"
            className="o-chip"
            aria-pressed={horizon === h}
            onClick={() => setHorizon(h)}
          >
            {h} days
          </button>
        ))}
      </div>

      <div className="o-split">
        <Card
          title={`Closing cash · ${horizon} days`}
          basis="model"
          asOf={data.run_date}
          ageClass="model"
          action={<Pill tone="neutral">scenario: {data.scenario}</Pill>}
        >
          <LineChart
            points={points}
            threshold={{ value: data.buffer_floor, label: 'buffer floor' }}
            markers={markers}
            title="Closing cash by day"
          />
          <div className="o-scorecard">
            <div className="o-kpi">
              <span className="o-kpi__label">Low point</span>
              <span className="o-kpi__value">{formatMoney(data.low_point.cents, { showCents: false })}</span>
              <span className="o-kpi__meta">{data.low_point.date ?? 'no date'}</span>
            </div>
            <div className="o-kpi">
              <span className="o-kpi__label">First breach</span>
              <span className={`o-kpi__value${data.first_breach_date ? ' o-kpi__value--crit' : ''}`}>
                {data.first_breach_date ?? 'none'}
              </span>
              <span className="o-kpi__meta">against the buffer floor</span>
            </div>
            <div className="o-kpi">
              <span className="o-kpi__label">Opening cash</span>
              <span className="o-kpi__value">{formatMoney(data.opening_cash_cents, { showCents: false })}</span>
              <span className="o-kpi__meta">bank plus gateway receivable</span>
            </div>
          </div>
          <p className="o-muted">
            Cash, not profit. This table is never rendered beside the accrual profit and loss,
            because they answer different questions and mixing them is how a profitable month
            misses a supplier payment.
          </p>
        </Card>

        <AssumptionsPanel assumptions={data.assumptions} digest={data.inputs_digest} />
      </div>

      <Card title="Daily table" basis="model" asOf={data.run_date} ageClass="model">
        <ForecastTable days={data.days} />
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */

const ASSUMPTION_LABELS: Record<string, string> = {
  payment_mix: 'Payment mix and payout lag',
  rr_redemption_rate: 'Store credit redemption rate',
  refund_rate: 'Refund rate',
  ad_daily_budget_cents: 'Daily ad budget',
  opening_cash_cents: 'Opening cash',
  buffer_floor_cents: 'Buffer floor',
  growth_factor: 'Revenue growth',
  profile_weeks: 'Weekday profile window',
};

export function AssumptionsPanel({ assumptions, digest }: { assumptions: Assumption[]; digest: string }) {
  if (assumptions.length === 0) {
    return (
      <Card title="Assumptions">
        <EmptyState message="This run recorded no assumptions." />
      </Card>
    );
  }

  return (
    <Card title="Assumptions" basis="model" action={<Pill tone="neutral">{digest.slice(0, 8)}</Pill>}>
      <div className="o-assumptions">
        {assumptions.map((assumption) => (
          <div key={assumption.key} style={{ display: 'contents' }}>
            <span>
              {ASSUMPTION_LABELS[assumption.key] ?? assumption.key.replace(/_/g, ' ')}{' '}
              <Pill tone={assumption.mode === 'override' ? 'copper' : 'neutral'}>
                {assumption.mode === 'override' ? 'OVERRIDE' : 'AUTO'}
              </Pill>
            </span>
            <span className="o-assumptions__value">{describeValue(assumption)}</span>
            <span className="o-assumptions__basis">
              {assumption.basis}
              {assumption.note ? ` · ${assumption.note}` : ''}
            </span>
          </div>
        ))}
      </div>
      <p className="o-muted">
        Overrides are set in Settings and are audited. Nothing on this page changes a number the
        model will use; the panel reports what the stored run was built from.
      </p>
    </Card>
  );
}

function describeValue(assumption: Assumption): string {
  const value = assumption.value;
  if (assumption.key.endsWith('_cents')) return formatMoney(Number(value), { showCents: false });
  if (assumption.key === 'payment_mix' && Array.isArray(value)) {
    return value
      .map((entry) => {
        const row = entry as { method?: string; share?: number; payout_lag_business_days?: number };
        return `${row.method ?? '?'} ${Math.round((row.share ?? 0) * 100)}% +${row.payout_lag_business_days ?? 0}bd`;
      })
      .join(', ');
  }
  if (typeof value === 'number') {
    return assumption.key.endsWith('_rate') ? `${(value * 100).toFixed(1)}%` : String(value);
  }
  return typeof value === 'string' ? value : JSON.stringify(value);
}

/* ------------------------------------------------------------------ */

export function ForecastTable({ days }: { days: ForecastDay[] }) {
  const columns: readonly Column<ForecastDay>[] = [
    { id: 'date', header: 'Date', priority: 1, mono: true, render: (d) => d.date },
    {
      id: 'opening',
      header: 'Opening',
      priority: 3,
      align: 'right',
      money: true,
      render: (d) => <MoneyText cents={d.opening_cents} showCents={false} />,
    },
    {
      id: 'inflow',
      header: 'In',
      priority: 2,
      align: 'right',
      money: true,
      render: (d) => <MoneyText cents={d.inflow_total_cents} showCents={false} />,
    },
    {
      id: 'outflow',
      header: 'Out',
      priority: 2,
      align: 'right',
      money: true,
      render: (d) => <MoneyText cents={-d.outflow_total_cents} showCents={false} />,
    },
    {
      id: 'closing',
      header: 'Closing',
      priority: 1,
      align: 'right',
      money: true,
      render: (d) => (
        <span className={d.below_buffer ? 'o-kpi__value--crit' : undefined}>
          <MoneyText cents={d.closing_cents} showCents={false} />
        </span>
      ),
    },
    {
      id: 'events',
      header: 'Named outflows',
      priority: 4,
      render: (d) => (d.events.length === 0 ? '' : d.events.map((e) => e.label).join(', ')),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={days}
      rowKey={(d) => d.date}
      caption="Daily cash forecast"
      emptyState={<EmptyState message="No days in this horizon." />}
      allowHorizontalScroll
    />
  );
}
